import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import BulkUploadTemplateSelector from "./BulkUploadTemplateSelector";
import FileUpload from "../../../components/FileUpload";
import { apiClient } from "../../../utils/axios";
import { showSnackbar } from "../../../api/snackbarSlice";
import { useCreateBulkUploadHandler } from "../dataHandlers/createBulkUploadHandler";
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { WRITE_BULK_UPLOAD } from '../../../constants/eInvoicePermissions';

const BulkUploadButton = ({
  category = "E_INVOICE",
  subCategory = null,
  redirectAfterSubmit,
  requiredPermission
}) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const subscription = useSelector((state) => state.subscription.subscription);
  const dispatch = useDispatch();
  const isBulkUploadFeatureEnabled = true;
  const isSftpFeatureEnabled = subscription?.features?.enable_sftp;
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { onSubmit, isPending } = useCreateBulkUploadHandler(() => {
    setOpen(false);
  }, redirectAfterSubmit);


  const isPermissionDenied =
      !hasEInvoicePermission(WRITE_BULK_UPLOAD) ||
      Boolean(requiredPermission && !hasEInvoicePermission(requiredPermission));

  const handleClose = () => {
    setOpen(false);
  };

  const getModalTitle = () => {
    if (category === "NON_E_INVOICE") {
      return "Bulk Upload Non-E-Invoice Documents";
    }
    return "Bulk Upload E-Invoice Documents";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) {
      alert("Please select a file.");
      return;
    }
    if (!selectedTemplate) {
      alert("Please select a template.");
      return;
    }
    onSubmit(file, category, subCategory, selectedTemplate);
  };

  const onTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const onFileUpload = (file) => {
    setFile(file);
  };

  const onDownloadTemplate = () => {
    setDownloadingTemplate(true);
    apiClient
      .get("/bulk-uploads/template/download", {
        responseType: "blob",
        params: { templateName: selectedTemplate },
      })
      .then((response) => {
        dispatch(
          showSnackbar({
            message: "Template downloaded successfully!",
            severity: "success",
          }),
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${selectedTemplate}.xlsx`);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          showActionDeniedSnackbar();
          return;
        }
        dispatch(
          showSnackbar({
            message: "Error downloading the template. Please try again later",
            severity: "error",
          }),
        );
      })
      .finally(() => {
        setDownloadingTemplate(false);
      });
  };

  if (!isBulkUploadFeatureEnabled && !isSftpFeatureEnabled) {
    return null;
  }

  const handleOpen = async () => {
    setOpen(true);
  };

  return (
    <>
      <PermissionDeniedWrapper show={isPermissionDenied} title={accessDeniedMessage}>
        <Button
          id="bulk-upload"
          variant="contained"
          color="primary"
          onClick={handleOpen}
          disabled={isPermissionDenied}
        >
          {"+ Bulk Upload"}
        </Button>
      </PermissionDeniedWrapper>
      <Modal open={open} onClose={handleClose}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            p: 2,
            boxShadow: 24,
            outline: "none",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold">
              {getModalTitle()}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Grid sx={{ pt: 1 }}>
            <Typography variant="subtitle2" sx={{ py: 1 }}>
              {"1. Select a template"}
            </Typography>
            <BulkUploadTemplateSelector
              onSelect={onTemplateSelect}
              category={category}
              subCategory={subCategory}
            />
          </Grid>
          <Grid sx={{ pt: 1 }}>
            <Typography variant="subtitle2" sx={{ py: 1 }}>
              {"2. Upload your file"}
            </Typography>
            <FileUpload onUpload={onFileUpload} supportedFormats={[".xlsx"]} />
            <Grid alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                {"Need the template? Download here:"}
              </Typography>
              <Button
                variant="text"
                color="primary"
                disabled={!selectedTemplate || downloadingTemplate}
                onClick={onDownloadTemplate}
                startIcon={
                  downloadingTemplate ? <CircularProgress size={20} /> : null
                }
              >
                {downloadingTemplate ? "Downloading..." : "Download Template"}
              </Button>
            </Grid>
          </Grid>
          <Divider />
          <Grid mt={2} textAlign="right">
            <Button
              id="upload-start-processing"
              variant="contained"
              color="primary"
              disabled={!file || isPending || downloadingTemplate}
              startIcon={isPending ? <CircularProgress size={20} /> : null}
              onClick={handleSubmit}
            >
              {isPending ? "Processing..." : "Start Processing"}
            </Button>
          </Grid>
        </Paper>
      </Modal>
    </>
  );
};

export default BulkUploadButton;
